import { CoinsVal } from "@components/icons/coins";
import { LoraSelect } from "@components/lora-select";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useChipHandlers } from "@features/studio/use-chip-handlers";
import {
  isThereEmptyZones,
  renderImage,
} from "@features/studio/use-render-image";
import { useStudio } from "@features/studio/use-studio";
import { useEffectPlease } from "@features/utils";
import { useSavedState } from "@features/utils/use-saved-state";
import { PaperClipIcon, XMarkIcon } from "@heroicons/react/16/solid";
import {
  Box,
  Button,
  Heading,
  IconButton,
  SegmentedControl,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { FileInputOrLink } from "@views/app/camera/components/file-input-or-link";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type EditMode = "image" | "model";

interface StudioEditObjectsProps {
  image: ImagesType;
}

export const StudioEditObjects = ({ image }: StudioEditObjectsProps) => {
  const { t } = useTranslation();
  const { studio, setStudio } = useStudio();
  const { extend } = useImage(image.id);
  const { chips, chipEdits, handleRemoveChip } = useChipHandlers();

  const [hasEmptyZones, setHasEmptyZones] = useState(false);
  const [prompt, setPrompt] = useSavedState("edit-prompt-" + image.id, "", {
    ttlMs: 7 * 86400000,
  });
  const [count, setCount] = useSavedState("edit-count", 1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useSavedState<EditMode>("edit-mode", "image");
  const [attachment, setAttachment] = useState<File | string | null>(null);
  const [selectedLoraId, setSelectedLoraId] = useState<string>("none");

  const textAreaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  useEffectPlease(() => {
    (async () =>
      setHasEmptyZones(
        await isThereEmptyZones(
          image,
          image?.size,
          undefined,
          studio?.drawing_svg_ref?.current || undefined
        )
      ))();
  }, [
    image.url,
    JSON.stringify(studio?.frame),
    JSON.stringify(studio?.drawing),
  ]);

  // Set drawing mode based on whether there's content
  // But don't override if we're in chips mode
  useEffectPlease(() => {
    if (studio?.edit_tool === "chips") return; // Don't override chips mode
    const hasContent =
      prompt.trim() ||
      attachment ||
      (selectedLoraId && selectedLoraId !== "none") ||
      chips.length > 0;
    setStudio({ edit_tool: hasContent ? "edit" : "remove" });
  }, [prompt, attachment, selectedLoraId, chips.length]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const urlToBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const handleGenerate = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const imageData =
        studio?.edit_tool === "chips"
          ? await renderImage(image, studio, {
              format: "jpg",
              chips: true,
              ignore_edited_image: true,
            })
          : await renderImage(image, studio, {
              format: "png",
              drawing: true,
              ignore_edited_image: true,
            });

      // Convert attachment to base64 (for draw mode)
      const attachmentBase64: string[] = [];
      if (mode === "image" && attachment && studio?.edit_tool !== "chips") {
        if (typeof attachment === "string") {
          const base64 = await urlToBase64(attachment);
          attachmentBase64.push(base64);
        } else {
          const base64 = await fileToBase64(attachment);
          attachmentBase64.push(base64);
        }
      }

      // Build chips data for chip mode
      let chipsData:
        | {
            number: number;
            x: number;
            y: number;
            prompt: string;
            attachment?: string;
            lora_id?: string;
          }[]
        | undefined;

      if (studio?.edit_tool === "chips" && chips.length > 0) {
        chipsData = chips.map((chip) => {
          const chipEdit = chipEdits.find((e) => e.chipId === chip.id);
          // Attachment is already a base64 string (converted on selection)
          return {
            number: chip.number,
            x: chip.x,
            y: chip.y,
            prompt: chipEdit?.prompt || "",
            attachment: chipEdit?.attachment || undefined,
            lora_id:
              chipEdit?.loraId && chipEdit.loraId !== "none"
                ? chipEdit.loraId
                : undefined,
          };
        });
      }

      // If debug in query string, show the image in a new tab
      if (window.location.search.includes("debug")) {
        const debugWindow = window.open();
        if (debugWindow) {
          debugWindow.document.write(
            `<img src="${imageData}" alt="Debug Image" />`
          );
          debugWindow.document.write(
            `<pre>${JSON.stringify(chipsData, null, 2)}</pre>`
          );
        }
        return;
      }

      await extend(
        imageData,
        studio?.edit_tool === "chips" ? undefined : prompt.trim() || undefined,
        count,
        attachmentBase64.length > 0 ? attachmentBase64 : undefined,
        studio?.edit_tool !== "chips" &&
          mode === "model" &&
          selectedLoraId &&
          selectedLoraId !== "none"
          ? selectedLoraId
          : undefined,
        chipsData
      );
    } catch (error) {
      console.error("Failed to process request:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const hasContent =
    prompt.trim() ||
    attachment ||
    (selectedLoraId && selectedLoraId !== "none");
  const hasChipsWithContent = chipEdits.some(
    (edit) =>
      edit.prompt.trim() ||
      edit.attachment ||
      (edit.loraId && edit.loraId !== "none")
  );
  const canGenerate = hasEmptyZones || hasContent || hasChipsWithContent;

  const getButtonText = () => {
    if (hasContent || hasChipsWithContent || !hasEmptyZones) {
      return t("studio.actions.edit.edit_add");
    }
    return t("studio.actions.edit.remove");
  };

  const getButtonColor = (): "blue" | "red" | "green" => {
    if (!hasContent && !hasChipsWithContent) return "red";
    if (mode === "model" && selectedLoraId && selectedLoraId !== "none")
      return "green";
    return "blue";
  };

  return (
    <div className="space-y-4">
      <Heading size="3">{t("studio.actions.edit.title")}</Heading>

      {/* Tool mode selector: Draw vs Markers */}
      <SegmentedControl.Root
        className="bg-slate-200 dark:bg-slate-900 w-full"
        value={studio?.edit_tool === "chips" ? "chips" : "draw"}
        onValueChange={(value) => {
          if (value === "chips") {
            setStudio({ edit_tool: "chips" });
          } else {
            // Reset to edit/remove based on content
            const hasContent =
              prompt.trim() ||
              attachment ||
              (selectedLoraId && selectedLoraId !== "none");
            setStudio({ edit_tool: hasContent ? "edit" : "remove" });
          }
        }}
        size="1"
      >
        <SegmentedControl.Item value="chips">
          {t("studio.actions.edit.mode_markers")}
        </SegmentedControl.Item>
        <SegmentedControl.Item value="draw">
          {t("studio.actions.edit.mode_draw")}
        </SegmentedControl.Item>
      </SegmentedControl.Root>

      <div className="space-y-3">
        {/* Chip markers - each marker has its own box when in markers mode */}
        {studio?.edit_tool === "chips" && (
          <div className="space-y-2">
            {chips.length === 0 && (
              <Text size="2" color="gray" className="block text-center py-4">
                {t("studio.actions.edit.chips_click_to_add")}
              </Text>
            )}
            <AnimatePresence>
              {chips.map((chip) => {
                const chipEdit = chipEdits.find((e) => e.chipId === chip.id);
                if (!chipEdit) return null;
                const showAttachmentSection = chipEdit.mode !== "none";
                return (
                  <motion.div
                    key={chip.id}
                    initial={{ scale: 0.8, opacity: 0, y: -10 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.8, opacity: 0, y: -10 }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  >
                    <Box className="p-3 bg-slate-100 dark:bg-slate-800 rounded-md space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 600,
                              damping: 15,
                              delay: 0.1,
                            }}
                            className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center font-bold"
                          >
                            {chip.number}
                          </motion.span>
                          <Text size="2" weight="medium">
                            {t("studio.actions.edit.chips_marker")}{" "}
                            {chip.number}
                          </Text>
                        </div>
                        <IconButton
                          size="1"
                          variant="ghost"
                          color="red"
                          onClick={() => handleRemoveChip(chip.id)}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </IconButton>
                      </div>
                      <TextArea
                        ref={(el) => {
                          textAreaRefs.current[chip.id] = el;
                        }}
                        placeholder={t(
                          "studio.actions.edit.chips_prompt_placeholder",
                          { number: chip.number }
                        )}
                        value={chipEdit.prompt}
                        onChange={(
                          e: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setStudio({
                            chipEdits: chipEdits.map((edit) =>
                              edit.chipId === chip.id
                                ? { ...edit, prompt: e.currentTarget.value }
                                : edit
                            ),
                          });
                        }}
                        rows={2}
                        autoFocus
                      />
                      {/* Collapsed attachment button or expanded section */}
                      {!showAttachmentSection ? (
                        <Button
                          size="1"
                          variant="soft"
                          color="gray"
                          className="w-full"
                          onClick={() => {
                            setStudio({
                              chipEdits: chipEdits.map((edit) =>
                                edit.chipId === chip.id
                                  ? { ...edit, mode: "image" }
                                  : edit
                              ),
                            });
                          }}
                        >
                          <PaperClipIcon className="w-4 h-4" />
                          {t("studio.actions.edit.chips_add_attachment")}
                        </Button>
                      ) : (
                        <>
                          <SegmentedControl.Root
                            className="bg-slate-200 dark:bg-slate-900"
                            value={chipEdit.mode}
                            onValueChange={(value) => {
                              setStudio({
                                chipEdits: chipEdits.map((edit) =>
                                  edit.chipId === chip.id
                                    ? {
                                        ...edit,
                                        mode: value as "image" | "model",
                                      }
                                    : edit
                                ),
                              });
                            }}
                            size="1"
                          >
                            <SegmentedControl.Item value="image">
                              {t("studio.actions.edit.mode_image")}
                            </SegmentedControl.Item>
                            <SegmentedControl.Item value="model">
                              {t("studio.actions.edit.mode_model")}
                            </SegmentedControl.Item>
                          </SegmentedControl.Root>
                          {chipEdit.mode === "image" && (
                            <FileInputOrLink
                              fromImage={chipEdit.attachment}
                              setFromImage={async (file) => {
                                // Convert File to base64 immediately for persistence
                                let attachmentValue: string | null = null;
                                if (file) {
                                  if (typeof file === "string") {
                                    attachmentValue = file;
                                  } else {
                                    // Convert File to base64
                                    attachmentValue = await fileToBase64(file);
                                  }
                                }
                                setStudio({
                                  chipEdits: chipEdits.map((edit) =>
                                    edit.chipId === chip.id
                                      ? { ...edit, attachment: attachmentValue }
                                      : edit
                                  ),
                                });
                              }}
                            />
                          )}
                          {chipEdit.mode === "model" && (
                            <LoraSelect
                              type="model"
                              value={chipEdit.loraId}
                              onChange={(value) => {
                                setStudio({
                                  chipEdits: chipEdits.map((edit) =>
                                    edit.chipId === chip.id
                                      ? { ...edit, loraId: value }
                                      : edit
                                  ),
                                });
                              }}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Global prompt input - only show in draw mode */}
        {studio?.edit_tool !== "chips" && (
          <>
            <TextArea
              placeholder={t("studio.actions.edit.prompt_placeholder")}
              value={prompt}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setPrompt(e.currentTarget.value)
              }
              rows={3}
            />

            <Box className="p-2 bg-slate-100 dark:bg-slate-800 rounded-md">
              {/* Mode selector */}
              <SegmentedControl.Root
                className="bg-slate-200 dark:bg-slate-900"
                value={mode}
                onValueChange={(value) => setMode(value as EditMode)}
                size="1"
              >
                <SegmentedControl.Item value="image">
                  {t("studio.actions.edit.mode_image")}
                </SegmentedControl.Item>
                <SegmentedControl.Item value="model">
                  {t("studio.actions.edit.mode_model")}
                </SegmentedControl.Item>
              </SegmentedControl.Root>

              {/* Image mode: Attachment */}
              {mode === "image" && (
                <FileInputOrLink
                  fromImage={attachment}
                  setFromImage={setAttachment}
                />
              )}

              {/* Model mode: Lora selector */}
              {mode === "model" && (
                <div className="space-y-2 mt-2">
                  <LoraSelect
                    type="model"
                    value={selectedLoraId}
                    onChange={(value) => setSelectedLoraId(value)}
                  />
                </div>
              )}
            </Box>
          </>
        )}

        {/* Hide button section in marker mode when no chips */}
        {!(studio?.edit_tool === "chips" && chips.length === 0) && (
          <div className="flex space-x-2 w-full">
            <ResponsiveSelect
              size="2"
              value={count.toString()}
              onChange={(value) => setCount(Number(value))}
              items={[
                { value: "1", label: "1" },
                { value: "2", label: "2" },
                { value: "3", label: "3" },
                { value: "4", label: "4" },
              ]}
              renderTrigger={() => (
                <span className="text-xs uppercase text-gray-500">{count}</span>
              )}
            />
            <div className="grow">
              <UpgradeNeeded minimumCredits={count * 4}>
                <Button
                  onClick={handleGenerate}
                  disabled={isProcessing || !canGenerate}
                  loading={isProcessing}
                  className="w-full"
                  color={getButtonColor()}
                >
                  {getButtonText()} <CoinsVal val={count * 4} />
                </Button>
              </UpgradeNeeded>
            </div>
          </div>
        )}

        {!hasEmptyZones &&
          !hasContent &&
          chips.length === 0 &&
          studio?.edit_tool !== "chips" && (
            <Text size="1" color="gray">
              {t("studio.actions.edit.draw_instruction")}
            </Text>
          )}

        <Text size="1" color="gray" className="block">
          {hasContent || hasChipsWithContent || !hasEmptyZones
            ? t("studio.actions.edit.edit_mode_description")
            : t("studio.actions.edit.remove_mode_description")}
        </Text>
      </div>
    </div>
  );
};
